import type { PalavraCanonica } from '../tipos';
import { inteiroAte, sementeDeTexto } from '../aleatorio/rng';
import type { IndiceLexico } from '../lexico/indice';

/**
 * A palavra do dia (§23.2).
 *
 * Ela é **função pura da data**, e não um arquivo escolhido por uma tarefa
 * agendada. Isso vale por três coisas: o jogo continua sem servidor e jogável
 * offline, não existe meia-noite em que um job pode falhar e deixar o dia sem
 * palavra, e qualquer data — passada ou futura — pode ser calculada em teste.
 * É a mesma disciplina do ADR-0004, agora com o dia no lugar da semente.
 *
 * O relógio entra como parâmetro: o núcleo não o lê (ADR-0001).
 */

/** Onde o calendário do jogo começa. Dia 0 da Trama. */
export const EPOCA = '2026-01-01';

/** O dia vira à meia-noite de Brasília, que é onde o jogo é jogado. */
export const FUSO = 'America/Sao_Paulo';

/**
 * Passo do sorteio.
 *
 * Não é `indice % tamanho`, que percorreria a lista em ordem alfabética e
 * daria dias seguidos começando pela mesma letra. Andando de `PASSO` em
 * `PASSO`, com o passo primo em relação ao tamanho da lista, a sequência é
 * uma permutação completa: nenhuma palavra se repete antes de todas terem
 * saído. Com 2937 palavras de cinco letras, são mais de oito anos sem repetir.
 */
const PASSO = 1_299_709;

/** Data no fuso do jogo, em `AAAA-MM-DD`. */
export function dataDoDia(agora: number): string {
  // `en-CA` formata como `AAAA-MM-DD`, que é o que se quer comparar e ordenar.
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: FUSO,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date(agora));
}

function paraUTC(data: string): number {
  const [ano, mes, dia] = data.split('-').map(Number);
  return Date.UTC(ano ?? 1970, (mes ?? 1) - 1, dia ?? 1);
}

const MS_POR_DIA = 86_400_000;

/** Quantos dias se passaram desde a época, contados no fuso do jogo. */
export function indiceDoDia(data: string): number {
  return Math.floor((paraUTC(data) - paraUTC(EPOCA)) / MS_POR_DIA);
}

/**
 * Deslocamento do fuso do jogo, em milissegundos, no instante dado.
 *
 * Sai da própria formatação, e não de um `-3` fixo: o Brasil não tem horário
 * de verão desde 2019, mas fixar o número seria apostar que ele não volta.
 */
function deslocamentoDoFuso(agora: number): number {
  const partes = new Intl.DateTimeFormat('en-CA', {
    timeZone: FUSO,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(new Date(agora));

  const parte = (tipo: string): number => Number(partes.find((p) => p.type === tipo)?.value ?? 0);
  const comoSeFosseUTC = Date.UTC(
    parte('year'),
    parte('month') - 1,
    parte('day'),
    parte('hour'),
    parte('minute'),
    parte('second'),
  );
  // `agora` carrega milissegundos que a formatação não devolve; descontá-los
  // deixa o deslocamento redondo, como todo fuso é.
  return comoSeFosseUTC - Math.floor(agora / 1000) * 1000;
}

/**
 * Milissegundos até a virada do dia, para o painel dizer quando vem a próxima.
 */
export function msAteAmanha(agora: number): number {
  const local = agora + deslocamentoDoFuso(agora);
  const decorrido = ((local % MS_POR_DIA) + MS_POR_DIA) % MS_POR_DIA;
  return MS_POR_DIA - decorrido;
}

/** O maior divisor comum, para garantir que o passo percorra a lista inteira. */
function mdc(a: number, b: number): number {
  let x = Math.abs(a);
  let y = Math.abs(b);
  while (y !== 0) [x, y] = [y, x % y];
  return x;
}

/**
 * Passo efetivo: `PASSO` quando ele é primo em relação ao tamanho da lista, e
 * o primeiro seguinte que for. Sem isso o ciclo se fecharia cedo e um punhado
 * de palavras se repetiria enquanto o resto nunca sairia.
 */
export function passoDaLista(tamanho: number): number {
  if (tamanho <= 1) return 1;
  let passo = PASSO % tamanho;
  if (passo === 0) passo = 1;
  while (mdc(passo, tamanho) !== 1) passo = (passo % tamanho) + 1;
  return passo;
}

/** Índice, dentro da lista de respostas, da palavra de um dia. */
export function indiceDaResposta(dia: number, tamanho: number): number {
  if (tamanho <= 0) return 0;
  const passo = passoDaLista(tamanho);
  // O dia pode ser negativo — alguém jogando antes da época, ou com o relógio
  // errado. O resto em JavaScript herda o sinal, então normaliza-se de volta.
  return (((dia * passo) % tamanho) + tamanho) % tamanho;
}

/**
 * Tamanhos que a palavra do dia pode ter (§23.1).
 *
 * Varia porque as palavras que o jogador escreve são livres: com a resposta
 * sempre de cinco letras, a dedução acabaria virando rotina. O tamanho é dito
 * no começo do dia — é uma incógnita a menos, não a mais.
 */
export const TAMANHOS_RESPOSTA = [4, 5, 6, 7] as const;

/** Tamanho da palavra de um dia. Determinístico, como todo o resto. */
export function tamanhoDoDia(data: string): number {
  const dia = indiceDoDia(data);
  const i = inteiroAte(sementeDeTexto(`tamanho ${dia}`), TAMANHOS_RESPOSTA.length);
  return TAMANHOS_RESPOSTA[i] ?? 5;
}

/** Respostas possíveis num dia: as palavras correntes daquele tamanho. */
export function respostasPossiveis(
  indice: IndiceLexico,
  tamanho: number,
): readonly PalavraCanonica[] {
  return indice.candidatasDeTamanho(tamanho);
}

/**
 * A palavra de um dia. `undefined` só quando não há lista — dicionário vazio.
 *
 * O passo primo garante que dias distintos caiam em índices distintos enquanto
 * o número do dia for menor que a lista. Como cada tamanho tem a sua lista, com
 * mais de mil palavras cada, isso cobre anos antes de a primeira repetição
 * poder acontecer.
 */
export function respostaDoDia(indice: IndiceLexico, data: string): PalavraCanonica | undefined {
  const lista = respostasPossiveis(indice, tamanhoDoDia(data));
  if (lista.length === 0) return undefined;
  return lista[indiceDaResposta(indiceDoDia(data), lista.length)];
}
